import React, { useState, useContext } from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, Button, Input, CircularProgress, IconButton,
	 DialogContent, DialogContentText, DialogActions,
	 FormHelperText, FormControl, InputLabel, Dialog} from '@material-ui/core';
import HeaderBar from './HeaderBar.js';
import { ArrowBack } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import firebase from '../Firebase.js';
import { AuthContext } from '../contexts/AuthContext.js';
import { Transition, isNameValid, isEmailValid, isPasswordValid, BottomMessage } from '../Common.js';

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      container: {
	marginTop: theme.spacing(1),
	overflow: 'auto',
	[theme.breakpoints.up('sm')] : {
	  height: `calc(100% - 64px)`
	},
	[theme.breakpoints.down('xs')] : {
	  height: `calc(100% - 56px)`
	},
      },
      link: {
	color: theme.palette.primary.green,
      },
      loginBtn: {
	borderRadius: 25,
	background: theme.palette.primary.yellow,
	color: theme.palette.primary.brown,
	margin:  theme.spacing(1),
	marginBottom:  theme.spacing(2),
	'&:hover': {
	  background: theme.palette.primary.yellow,
	},
      },
      loading: {
	marginTop: theme.spacing(1),
	marginBottom: theme.spacing(2),
      },
      successMessage: {
	background: theme.palette.primary.yellow,
	padding:  theme.spacing(2),
	margin:  theme.spacing(1),
	borderRadius: 20,
      },
      inputBox: {
	paddingRight: theme.spacing(1),
	paddingLeft: theme.spacing(1),
      }
    }),
);

const BackBtn = withRouter(({ history, signingIn }) => {
  return (
    <IconButton
      color='inherit'
      aria-label='open drawer'
      onClick={() => {
	if (!signingIn) {
	  history.goBack();
	}
      }}
      edge='end'>
      <ArrowBack/>
    </IconButton>
  );
});

export default () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [signingIn, setSigningIn] = useState(false);
  const { isAuthTrainerOrAdmin } = useContext(AuthContext);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const resetInputs = () => {
    setName('');
    setEmail('');
    setPassword('');
    setPasswordRepeat('');
  };

  const register = async () => {
    setSigningIn(true);
    try {
      await firebase.auth().createUserWithEmailAndPassword(email, password);
      await Promise.all([firebase.auth().currentUser.updateProfile({displayName: name}),
			 firebase.auth().currentUser.sendEmailVerification()]);
      setSigningIn(false);
      setSuccessMessage(t('verify-your-email'));
      resetInputs();
      firebase.auth().signOut();
    }
    catch (error) {
      setErrorMessage(error.code);
      resetInputs();
      setSigningIn(false);
      console.log('error registering ', error);
    }
  };

  if (isAuthTrainerOrAdmin()) {
    return <Redirect to={{ pathname: '/exams' }} />;
  }
  else {
    return (
      <div style={{height: '100%'}}>
	<HeaderBar LComp={<BackBtn signingIn={signingIn}/>}
		   MComp={<Typography variant='h6' align='center'>{t('register')}</Typography>}
		   RComp={<div/>}/>
	<Grid container
	      wrap='nowrap'
	      direction='column'
	      alignItems='center'
	      className={classes.container}>
	  <Grid item container direction='column' className={classes.inputBox}>
	    <FormControl color='secondary' required>
	      <InputLabel> {t('name')} </InputLabel>
	      <Input value={name}
		     onChange={(e) => setName(e.target.value)} />
	      <FormHelperText>
		{name && !isNameValid(name) ? t('must-be-at-least-2-chars') : ''}
	      </FormHelperText>
	    </FormControl>
	  </Grid>
	  <Grid item container direction='column' className={classes.inputBox}>
	    <FormControl required color='secondary'>
	      <InputLabel> {t('email')} </InputLabel>
	      <Input value={email}
		     onChange={(e) => setEmail(e.target.value)}
		     type='email'/>
	      <FormHelperText>
		{email && !isEmailValid(email) ? t('email-not-valid') : ''}
	      </FormHelperText>
	    </FormControl>
	  </Grid>
	  <Grid item container direction='column' className={classes.inputBox}>
	    <FormControl required color='secondary'>
	      <InputLabel> {t('password')} </InputLabel>
	      <Input value={password}
		     onChange={(e) => setPassword(e.target.value)}
		     type='password' />
	      <FormHelperText>
		{password && !isPasswordValid(password) ? t('must-be-at-least-6-chars') : ''}
	      </FormHelperText>
	    </FormControl>
	  </Grid>
	  <Grid item container direction='column' className={classes.inputBox}>
	    <FormControl required color='secondary'>
	      <InputLabel> {t('repeat-password')} </InputLabel>
	      <Input value={passwordRepeat}
		     onChange={(e) => setPasswordRepeat(e.target.value)}
		     type='password'/>
	      <FormHelperText>
		{passwordRepeat && (password !== passwordRepeat) ? t('passwords-do-not-match') : ''}
	      </FormHelperText>
	    </FormControl>
	  </Grid>
	  <Grid item>
	    { signingIn ? (<CircularProgress color='secondary' className={classes.loading} />) :
	      (<Button label='sign-out'
		       size='large'
		       disabled={!isEmailValid(email)
				 || !isNameValid(name)
				 || !isPasswordValid(password)
				 || !isPasswordValid(passwordRepeat)
				 || (password !== passwordRepeat)}
		       onClick={() => register()}
		       variant='contained'
		       className={classes.loginBtn}>
		 { t('register') }
	       </Button>)}
	  </Grid>
	  <BottomMessage open={!!errorMessage}
			 message={errorMessage}
			 close={() => setErrorMessage(null)}/>
	  <Dialog
	    open={!!successMessage}
	    onClose={() => setSuccessMessage(null)}
	    TransitionComponent={Transition}>
	    <DialogContent>
	      <DialogContentText>
		{t('great-you-have-registered-as-dogo-trainer')}
	      </DialogContentText>
	      <DialogContentText>
		{ successMessage }
	      </DialogContentText>
	    </DialogContent>
	    <DialogActions>
	      <Button onClick={() => setSuccessMessage(null)} color='secondary'>
		{t('close')}
	      </Button>
	    </DialogActions>
	  </Dialog>
	</Grid>
      </div>
    );
  }
};
